import { useRef, forwardRef, useImperativeHandle } from 'react';
import Turnstile from 'react-turnstile';

const TURNSTILE_SITE_KEY = '0x4AAAAAACEVlOFdlQszMYCq';

/**
 * Cloudflare Turnstile Widget Component
 * 
 * @param {Object} props
 * @param {string} props.onSuccess - Callback when verification succeeds
 * @param {string} props.onError - Callback when verification fails
 * @param {string} props.mode - 'strict' or 'soft' (default: 'strict')
 */
const TurnstileWidget = forwardRef(({ onSuccess, onError, mode = 'strict' }, ref) => {
  const turnstileRef = useRef(null);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getToken: () => {
      return turnstileRef.current?.getResponse();
    },
    reset: () => {
      turnstileRef.current?.reset();
    }
  }));

  const handleVerify = (token) => {
    // Called when verification completes (with or without pre-clearance)
    if (onSuccess) {
      onSuccess(token);
    }
  };

  const handleSuccess = (token, preClearanceObtained) => {
    // Called when pre-clearance is obtained (only if pre-clearance is enabled)
    if (onSuccess) {
      onSuccess(token);
    }
  };

  const handleError = () => {
    if (onError) {
      onError();
    }
    
    // Soft mode: just log error but don't block
    if (mode === 'soft') {
      console.warn('[Turnstile] Verification failed in soft mode');
    }
  };

  return (
    <div className="turnstile-widget my-4">
      <Turnstile
        ref={turnstileRef}
        sitekey={TURNSTILE_SITE_KEY}
        onVerify={handleVerify}
        onSuccess={handleSuccess}
        onError={handleError}
        onExpire={() => turnstileRef.current?.reset()}
        options={{
          theme: 'light',
          size: 'normal',
          language: 'de',
        }}
      />
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
